import { useEffect, useRef } from "react";
import { HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { tokenIsDue, useDolfinAuth } from "@/components/DolfinProvider";
import type { components } from "@dolfin/api-admin";

type BillState = components["schemas"]["BillState"];
type BillStateChange = { billId: string; fromState: BillState; toState: BillState };
type BillClassified = { billId: string };
type BillPaymentStatusChanged = {
  billPaymentId: string;
  billId: string;
  status: string;
  isoStatus?: string | null;
  multiAuthorisationStatus?: string | null;
};

/**
 * WebSocket upgrades can't carry headers, so the org id rides on the URL and the token on accessTokenFactory.
 * `billClassified` arrives without a state change, so keep `enabled` through PendingReview. No-op without `hubBaseUrl`.
 */
export function useBillStateChange(
  billId: string | undefined,
  enabled: boolean,
  onChange: (msg?: BillStateChange) => void,
  options: { hubBaseUrl?: string } = {},
) {
  const { auth, organisationId, refresh } = useDolfinAuth();
  const hasToken = !!auth?.token;
  // Read at (re)connect time rather than captured, so a token refresh doesn't tear the connection down.
  const tokenRef = useRef(auth?.token);
  tokenRef.current = auth?.token;
  const hubBaseUrl = options.hubBaseUrl;

  useEffect(() => {
    if (!enabled || !billId || !hubBaseUrl || !hasToken || !organisationId) return;

    const hubUrl = `${hubBaseUrl}/hubs/bills?organisationId=${encodeURIComponent(organisationId)}`;

    const connection = new HubConnectionBuilder()
      // withCredentials must be false: the API's Public CORS omits Allow-Credentials, so the browser blocks negotiate.
      .withUrl(hubUrl, {
        accessTokenFactory: async () => {
          const current = tokenRef.current;
          if (current && !tokenIsDue(current)) return current;
          return (await refresh())?.token ?? current ?? "";
        },
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    const stateHandler = (msg: BillStateChange) => {
      if (msg.billId === billId) onChange(msg);
    };
    const classifiedHandler = (msg: BillClassified) => {
      if (msg.billId === billId) onChange();
    };
    // Pay-by-bank status moves don't necessarily change the bill state; refetch to show progress.
    const paymentStatusHandler = (msg: BillPaymentStatusChanged) => {
      if (msg.billId === billId) onChange();
    };
    connection.on("billStateChanged", stateHandler);
    connection.on("billClassified", classifiedHandler);
    connection.on("billPaymentStatusChanged", paymentStatusHandler);

    connection.start().catch((err) => console.error("SignalR connect failed", err));

    return () => {
      connection.off("billStateChanged", stateHandler);
      connection.off("billClassified", classifiedHandler);
      connection.off("billPaymentStatusChanged", paymentStatusHandler);
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
    };
  }, [billId, enabled, hubBaseUrl, hasToken, organisationId, onChange, refresh]);
}
